import { useEffect, useRef, useState, useMemo } from "react";
import {
  Translate, X, SpeakerHigh, Pause, Play, Stop, MagnifyingGlass, ArrowClockwise,
} from "@phosphor-icons/react";

/**
 * GlobalTranslateWidget — a floating Translate + Listen bubble that appears
 * on every page of the site. Translates the entire visible page into any
 * of 30+ languages via Google's free public gtx endpoint (no API key), and
 * reads the page aloud via the browser SpeechSynthesis API.
 *
 * Mounted once in App.js so users can access it from any surface —
 * FarmersPulseHome, FarmersToday, Guides, Legal, About, Weather, News, etc.
 *
 * Persists the selected language across navigations via localStorage.
 */
const LANGUAGES = [
  { code: "en", label: "English",     flag: "🇺🇸" },
  { code: "es", label: "Español",     flag: "🇪🇸" },
  { code: "fr", label: "Français",    flag: "🇫🇷" },
  { code: "de", label: "Deutsch",     flag: "🇩🇪" },
  { code: "it", label: "Italiano",    flag: "🇮🇹" },
  { code: "pt", label: "Português",   flag: "🇵🇹" },
  { code: "nl", label: "Nederlands",  flag: "🇳🇱" },
  { code: "sv", label: "Svenska",     flag: "🇸🇪" },
  { code: "pl", label: "Polski",      flag: "🇵🇱" },
  { code: "ru", label: "Русский",     flag: "🇷🇺" },
  { code: "uk", label: "Українська",  flag: "🇺🇦" },
  { code: "tr", label: "Türkçe",      flag: "🇹🇷" },
  { code: "ar", label: "العربية",     flag: "🇸🇦" },
  { code: "fa", label: "فارسی",       flag: "🇮🇷" },
  { code: "he", label: "עברית",       flag: "🇮🇱" },
  { code: "hi", label: "हिन्दी",       flag: "🇮🇳" },
  { code: "bn", label: "বাংলা",       flag: "🇧🇩" },
  { code: "ta", label: "தமிழ்",        flag: "🇮🇳" },
  { code: "te", label: "తెలుగు",       flag: "🇮🇳" },
  { code: "mr", label: "मराठी",       flag: "🇮🇳" },
  { code: "ur", label: "اردو",        flag: "🇵🇰" },
  { code: "pa", label: "ਪੰਜਾਬੀ",       flag: "🇮🇳" },
  { code: "gu", label: "ગુજરાતી",     flag: "🇮🇳" },
  { code: "kn", label: "ಕನ್ನಡ",       flag: "🇮🇳" },
  { code: "ml", label: "മലയാളം",     flag: "🇮🇳" },
  { code: "id", label: "Indonesia",   flag: "🇮🇩" },
  { code: "ms", label: "Melayu",      flag: "🇲🇾" },
  { code: "vi", label: "Tiếng Việt",  flag: "🇻🇳" },
  { code: "th", label: "ไทย",         flag: "🇹🇭" },
  { code: "zh-CN", label: "简体中文",  flag: "🇨🇳" },
  { code: "zh-TW", label: "繁體中文",  flag: "🇹🇼" },
  { code: "ja", label: "日本語",       flag: "🇯🇵" },
  { code: "ko", label: "한국어",       flag: "🇰🇷" },
  { code: "sw", label: "Kiswahili",   flag: "🇰🇪" },
  { code: "am", label: "አማርኛ",       flag: "🇪🇹" },
  { code: "yo", label: "Yorùbá",      flag: "🇳🇬" },
  { code: "ha", label: "Hausa",       flag: "🇳🇬" },
  { code: "zu", label: "isiZulu",     flag: "🇿🇦" },
];

const STORAGE_LANG = "np:global-lang";

const TRANSLATABLE_SELECTOR =
  "h1, h2, h3, h4, h5, h6, p, li, a, button, span, label, small, blockquote, td, th, figcaption";

import { http } from "@/lib/api";

async function proxyTranslate(text, target) {
  if (!text) return text;
  try {
    const { data } = await http.post("/tx", { text, target, source: "auto" });
    return (data?.results?.[0] ?? text) || text;
  } catch (e) {
    return text;
  }
}

// Batched wrapper — sends up to 40 items in a single request so a page
// with hundreds of nodes translates in a few round-trips instead of dozens.
async function proxyTranslateBatch(items, target) {
  if (!items?.length) return [];
  try {
    const { data } = await http.post("/tx", { items, target, source: "auto" });
    const out = data?.results || [];
    return items.map((v, i) => out[i] ?? v);
  } catch (e) {
    return items;
  }
}

// Walk the page, cache original strings, replace visible text with target.
async function translatePage(target, restore = false) {
  const root = document.querySelector("main") || document.body;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const s = (node.nodeValue || "").trim();
      if (!s) return NodeFilter.FILTER_REJECT;
      if (s.length < 2) return NodeFilter.FILTER_REJECT;
      // skip script / style / hidden
      let el = node.parentElement;
      while (el) {
        const tag = el.tagName;
        if (!tag) break;
        if (["SCRIPT", "STYLE", "NOSCRIPT", "CODE"].includes(tag)) return NodeFilter.FILTER_REJECT;
        if (el.dataset?.notranslate === "true") return NodeFilter.FILTER_REJECT;
        // skip elements outside viewport-friendly containers
        el = el.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) nodes.push(n);

  if (restore) {
    for (const t of nodes) {
      if (t.__np_orig != null) { t.nodeValue = t.__np_orig; delete t.__np_orig; }
    }
    return;
  }

  // Batch: send groups of 40 through the backend proxy — one HTTP round
  // trip per batch. The proxy handles caching + Claude LLM fallback.
  const orig = [];
  for (const t of nodes) {
    if (t.__np_orig == null) t.__np_orig = t.nodeValue;
    orig.push(t.__np_orig);
  }
  const BATCH = 40;
  let idx = 0;
  for (let i = 0; i < orig.length; i += BATCH) {
    const batch = orig.slice(i, i + BATCH);
    const parts = await proxyTranslateBatch(batch, target);
    for (let k = 0; k < batch.length; k++) {
      nodes[idx].nodeValue = parts[k] ?? batch[k];
      idx += 1;
    }
  }
}

function readVisibleText() {
  const root = document.querySelector("main") || document.body;
  return (root.innerText || "").replace(/\s+/g, " ").slice(0, 6000);
}

export default function GlobalTranslateWidget() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(STORAGE_LANG) || "en"; } catch { return "en"; }
  });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [state, setState] = useState("idle"); // idle | playing | paused
  const utterRef = useRef(null);

  const activeLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return LANGUAGES;
    return LANGUAGES.filter(
      (l) => l.label.toLowerCase().includes(s) || l.code.toLowerCase().includes(s),
    );
  }, [q]);

  useEffect(() => { try { localStorage.setItem(STORAGE_LANG, lang); } catch {} }, [lang]);

  // Whenever the route changes, reapply the currently selected language so
  // the user doesn't have to re-pick after each navigation.
  useEffect(() => {
    if (lang === "en") return;
    const on = () => {
      // Small delay so the new page can paint first.
      setTimeout(() => { translatePage(lang).catch(() => {}); }, 400);
    };
    window.addEventListener("popstate", on);
    // React Router doesn't fire popstate on internal navs — patch history.
    const _push = window.history.pushState;
    window.history.pushState = function (...args) {
      _push.apply(this, args);
      window.dispatchEvent(new Event("popstate"));
    };
    return () => {
      window.removeEventListener("popstate", on);
      window.history.pushState = _push;
    };
  }, [lang]);

  const applyLang = async (code) => {
    setLang(code);
    setOpen(false);
    setQ("");
    if (code === "en") {
      setBusy(true);
      await translatePage("en", true).catch(() => {});
      setBusy(false);
      return;
    }
    setBusy(true);
    try { await translatePage(code); } finally { setBusy(false); }
  };

  const reapply = async () => {
    if (lang === "en") return;
    setBusy(true);
    try { await translatePage(lang); } finally { setBusy(false); }
  };

  const play = () => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(readVisibleText());
    u.lang = lang;
    u.onend = () => setState("idle");
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  };
  const pause = () => { window.speechSynthesis.pause(); setState("paused"); };
  const resume = () => { window.speechSynthesis.resume(); setState("playing"); };
  const stop = () => { window.speechSynthesis.cancel(); setState("idle"); };

  return (
    <div
      data-testid="global-translate"
      data-notranslate="true"
      className="fixed z-[80] bottom-4 right-4 sm:bottom-5 sm:right-5 flex flex-col items-end gap-2"
    >
      {open && (
        <div
          data-testid="global-translate-panel"
          className="w-[300px] max-h-[420px] rounded-2xl border border-black/10 bg-white shadow-2xl overflow-hidden animate-in fade-in duration-150"
        >
          <div className="px-3 py-2 flex items-center gap-2 border-b border-black/10 bg-[#F5EBDC]">
            <Translate size={13} weight="fill" className="text-[#2D8659]" />
            <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#0F3D2A] flex-1">
              Translate this page
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="global-translate-close"
              className="h-6 w-6 rounded-full hover:bg-black/5 flex items-center justify-center"
              aria-label="Close"
            >
              <X size={12} weight="bold" />
            </button>
          </div>

          <div className="px-3 pt-2">
            <div className="flex items-center gap-1 rounded-full border border-black/10 bg-white px-2 py-1">
              <MagnifyingGlass size={11} weight="bold" className="text-[#3A3830]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="global-translate-search"
                placeholder="Search language…"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#8E8B7F]"
              />
            </div>
          </div>

          <div
            data-testid="global-translate-list"
            className="mt-2 max-h-[240px] overflow-y-auto pb-2"
          >
            {filtered.map((l) => (
              <button
                key={l.code}
                onClick={() => applyLang(l.code)}
                data-testid={`global-translate-lang-${l.code}`}
                className={`w-full flex items-center gap-2 text-left px-4 py-2 text-sm hover:bg-[#F5EBDC] transition-colors ${
                  lang === l.code ? "font-semibold text-[#2D8659] bg-[#2D8659]/5" : "text-[#241209]"
                }`}
              >
                <span aria-hidden className="text-base">{l.flag}</span>
                <span className="flex-1 truncate">{l.label}</span>
                <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#8E8B7F]">
                  {l.code}
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-3 text-sm text-[#8E8B7F]">No languages match "{q}".</div>
            )}
          </div>

          <div className="px-3 py-2 border-t border-black/10 bg-[#F5EBDC]/50 flex items-center gap-1">
            {state === "idle" ? (
              <button
                onClick={play}
                data-testid="global-translate-listen"
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#0F3D2A] hover:border-[#2D8659] hover:text-[#2D8659]"
              >
                <SpeakerHigh size={10} weight="fill" /> Listen page
              </button>
            ) : (
              <>
                {state === "playing" ? (
                  <button onClick={pause} data-testid="global-translate-pause"
                    className="inline-flex items-center gap-1 rounded-full bg-[#0F3D2A] text-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                    <Pause size={10} weight="fill" /> Pause
                  </button>
                ) : (
                  <button onClick={resume} data-testid="global-translate-resume"
                    className="inline-flex items-center gap-1 rounded-full bg-[#0F3D2A] text-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                    <Play size={10} weight="fill" /> Resume
                  </button>
                )}
                <button onClick={stop} data-testid="global-translate-stop"
                  className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#0F3D2A]">
                  <Stop size={10} weight="fill" />
                </button>
              </>
            )}
            <div className="flex-1" />
            {lang !== "en" && (
              <button
                onClick={reapply}
                data-testid="global-translate-reapply"
                title="Retranslate this page"
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#0F3D2A] hover:border-[#2D8659] hover:text-[#2D8659]"
              >
                <ArrowClockwise size={10} weight="bold" /> Retranslate
              </button>
            )}
          </div>
        </div>
      )}

      <div
        data-testid="global-translate-bar"
        className="inline-flex items-center gap-1 rounded-full bg-[#0F3D2A] text-[#F5EBDC] p-1 shadow-lg"
      >
        {state === "idle" ? (
          <button
            onClick={play}
            disabled={busy}
            data-testid="global-listen-quick"
            title="Listen to this page"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors disabled:opacity-60"
          >
            <SpeakerHigh size={13} weight="fill" />
            <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase">Listen</span>
          </button>
        ) : (
          <>
            {state === "playing" ? (
              <button
                onClick={pause}
                data-testid="global-listen-pause-quick"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5"
              >
                <Pause size={13} weight="fill" />
                <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase">Pause</span>
              </button>
            ) : (
              <button
                onClick={resume}
                data-testid="global-listen-resume-quick"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5"
              >
                <Play size={13} weight="fill" />
                <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase">Resume</span>
              </button>
            )}
            <button
              onClick={stop}
              data-testid="global-listen-stop-quick"
              className="inline-flex items-center rounded-full px-2 py-1.5 hover:bg-white/10"
              aria-label="Stop"
              title="Stop"
            >
              <Stop size={13} weight="fill" />
            </button>
          </>
        )}

        <span aria-hidden className="h-4 w-px bg-white/25" />

        <button
          onClick={() => setOpen((v) => !v)}
          disabled={busy}
          data-testid="global-translate-toggle"
          title="Translate this page"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors disabled:opacity-60"
        >
          <span aria-hidden className="text-sm leading-none">{activeLang.flag}</span>
          <Translate size={13} weight="fill" />
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase">
            {busy ? "Translating…" : `Translate · ${activeLang.label}`}
          </span>
        </button>
      </div>
    </div>
  );
}
